import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfirmModal, Input } from '@bettercomp/ui-components';
import { Col, Divider, Row, Typography } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import cn from './styles.module.scss';
import { AuthErrors, AuthForm } from '../common/AuthForm';
import { showNotification } from 'common/function';
import {
  HRISAuthProvider,
  HRISConfigName,
  HRISIntegrationType,
} from 'types';
import { useHrisConfigClient } from 'hooks/useHrisConfigClient';
import {
  CreateHrisIntegrationRequest,
  HRISAuth,
  HRISIntegration,
} from 'service/hrisConfigClient';
import { isScheduleValid, isUrlValid, ScheduleControl } from 'components/UserSettings/HRIS/common/ScheduleControl';
import { reportError } from 'utils/reporting';
import { Loader } from 'components/Loader';
import { TimezoneSelector } from 'components/UserSettings/HRIS/common/TimezoneSelector';
import { isApiError } from 'common/types';
import { SectionHeader } from 'components/UserSettings/common/SectionHeader';

type Errors = {
  schedule?: string,
  timezone?: string,
} & AuthErrors

const DATA_TYPES = ['jobs', 'employees', 'ranges'] as const;
type WorkdayDataType = typeof DATA_TYPES[number];

// Friendly labels for the data types (reports) the sync endpoint reports back.
const DATA_TYPE_LABELS: Record<WorkdayDataType, string> = {
  jobs: 'Jobs',
  employees: 'Employees',
  ranges: 'Ranges',
};

// The integration config field that holds each report type's URL — used to tell a
// configured-but-not-activated report (URL set, source not pointed at Workday) apart
// from one that was never set up at all.
const DATA_TYPE_CONFIG: Record<WorkdayDataType, HRISConfigName> = {
  jobs: HRISConfigName.JOBS_CONFIG,
  employees: HRISConfigName.EMPLOYEES_CONFIG,
  ranges: HRISConfigName.RANGES_CONFIG,
};

function isValidForm(form: Partial<HRISIntegration>): form is CreateHrisIntegrationRequest {
  return !!form.auth && !!form.schedule && !!form.timezone;
}

const validateForm = (form: Partial<HRISIntegration>): Errors => {
  const errors: Errors = {};
  if (!form.schedule) {
    errors.schedule = 'Required';
  }
  if (form.schedule && !isScheduleValid(form.schedule)) {
    errors.schedule = 'All fields are required';
  }
  if (!form.timezone) {
    errors.timezone = 'Required';
  }
  if (!form.auth?.config?.url) {
    errors.url = 'Required';
  }

  if (form.type === HRISIntegrationType.WORKDAY_RAAS && form.config?.url && !isUrlValid(form.config.url)) {
    errors.url = 'Url is not valid. format=csv is allowed only';
  }

  if (!form.auth?.config?.client_id) {
    errors.client_id = 'Required';
  }

  if (!form.auth?.config?.client_secret) {
    errors.client_secret = 'Required';
  }

  if (!form.auth?.config?.grant_type) {
    errors.grant_type = 'Required';
  }

  if (!form.auth?.config?.refresh_token) {
    errors.refresh_token = 'Required';
  }

  return errors;
}

export function getConfigTestCopyText(configName: HRISConfigName): string {
  switch(configName){
    case HRISConfigName.JOBS_CONFIG:
      return 'Jobs report API endpoint'
    case HRISConfigName.EMPLOYEES_CONFIG:
      return 'Employees report API endpoint';
    case HRISConfigName.RANGES_CONFIG:
      return 'Ranges report API endpoint'
    default:
      return configName
  }
}

export const WoorkdayRaaS = () => {
  const hris = useHrisConfigClient();
  const [loadingTest, setLoadingTest] = useState(false);
  const [loadingSync, setLoadingSync] = useState(false);
  const [integration, setIntegration] = useState<Partial<HRISIntegration>>();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  // Gates "Pull Reports Now": a pull uses the persisted config, so unsaved edits
  // must be saved first. Set on any field change, cleared on load + successful save.
  const [dirty, setDirty] = useState(false);
  // Child form controls (notably react-js-cron's <Cron>) fire onChange once on
  // mount to sync their normalized value. That echo would flip `dirty` true right
  // after load, so we only arm dirty-tracking once the loaded form has settled.
  const dirtyTrackingArmed = useRef(false);

  const markDirty = () => {
    if (dirtyTrackingArmed.current) {
      setDirty(true);
    }
  };

  const loadIntegration = async () => {
    setLoading(true);
    try {
      const integration = await hris.listIntegrations({
        type: HRISIntegrationType.WORKDAY_RAAS,
        includeAuth: true,
      });
      dirtyTrackingArmed.current = false;
      setIntegration(integration[0]);
      setDirty(false);
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Load Workday RaaS integration error', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadIntegration();
  }, []);

  // Arm dirty-tracking once the load settles, whether or not an integration
  // exists (the create flow starts with none). Child mount-time onChange echoes
  // run in child effects, before this parent effect, so they land while tracking
  // is disarmed and can't mark the pristine form dirty.
  useEffect(() => {
    if (!loading && !dirtyTrackingArmed.current) {
      dirtyTrackingArmed.current = true;
      setDirty(false);
    }
  }, [loading]);

  const testDisabled = useMemo(() => {
    const {client_id, client_secret, url, refresh_token, grant_type} = integration?.auth?.config || {};

    return !client_id || !client_secret || !url || !refresh_token || !grant_type;
  }, [integration?.auth]);

  const handleAuthChange = (auth: HRISAuth) => {
    setErrors({});
    markDirty();
    setIntegration({
      ...integration,
      auth,
    });
  };

  const handleChange = (value: string, name: 'schedule' | 'timezone') => {
    // ScheduleControl's react-js-cron re-emits its synced/normalized value on mount
    // and again when the value prop settles; ignore echoes that don't change the
    // field so a controlled-input sync can't flip the form dirty on load.
    if (integration?.[name] === value) {
      return;
    }
    setErrors({});
    markDirty();
    setIntegration({
      ...integration,
      [name]: value,
    })
  }

  const testFileEndpoint = async (configName: HRISConfigName) => {
    const url = integration?.[configName]?.url
    if (integration?.auth?.provider === HRISAuthProvider.OAUTH2 && url) {

      if (!isUrlValid(url)) {
        showNotification('Failure', 'Url is not valid. format=csv is allowed only', 'error');
        return;
      }

      setLoadingTest(true);
      try {
        const response = await hris.testUrl({
          requestBody: {
            auth: integration.auth,
            url: integration[configName]?.url,
            configName,
          }
        });

        if (response.result === 'success') {
          showNotification(`${getConfigTestCopyText(configName)} test job started successfully. You will be notified when complete.`, 'HRIS Test Started', 'success');
        } else {
          const message = response.message || `Request failed with status code ${response.status}`;
          showNotification(message, 'HRIS Test Failed', 'error')
        }
      } catch (err: any) {
        reportError(err);
        showNotification(isApiError(err) ? (err.body.error || err.body.message) : err.message, 'Failed to test URL', 'error');
      }
      setLoadingTest(false);
    }
  }

  const handleSync = async () => {
    if (!integration?.id) {
      return;
    }
    setLoadingSync(true);
    try {
      const { dataTypes } = await hris.syncIntegration({ id: integration.id });

      if (dataTypes === undefined) {
        // Mixed-version deploy window: an older api build omits dataTypes but still
        // enqueued the pull — don't misreport that as "nothing to pull".
        showNotification('Workday report pull started.', 'Pull Started', 'success');
        return;
      }

      // Fall back to the raw key if a new data type ships before its label — degrades
      // to "ranges" rather than "undefined" in the toast.
      const pullingLabels = dataTypes.map((dataType) => DATA_TYPE_LABELS[dataType] ?? dataType);
      // Reports whose URL is configured but whose source isn't pointed at this Workday
      // integration — set up, but not activated for pulling. Report types without a URL
      // are excluded (never set up), so we don't nag about them.
      const notActivatedLabels = DATA_TYPES
        .filter((dataType) => !dataTypes.includes(dataType) && !!integration?.[DATA_TYPE_CONFIG[dataType]]?.url)
        .map((dataType) => DATA_TYPE_LABELS[dataType]);

      if (pullingLabels.length === 0 && notActivatedLabels.length === 0) {
        showNotification(
          'No reports are set to pull from Workday. On the Configuration tab, set a data type’s source to this Workday integration, then try again.',
          'Nothing to pull',
          'warning',
        );
        return;
      }

      showNotification(
        (
          <>
            {pullingLabels.length > 0 && (
              <div>Pulling from Workday: {pullingLabels.join(', ')}.</div>
            )}
            {notActivatedLabels.length > 0 && (
              <div>
                <Typography.Text type="warning">
                  <WarningFilled /> Not pulling {notActivatedLabels.join(', ')}. Activate {notActivatedLabels.length > 1 ? 'them' : 'it'} on the Configuration tab.
                </Typography.Text>
              </div>
            )}
          </>
        ),
        dataTypes.length ? 'Pull Started' : 'Nothing to pull',
        dataTypes.length ? 'success' : 'warning',
      );
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Failed to start sync', 'error');
    } finally {
      setLoadingSync(false);
    }
  };

  const handleSave = async () => {
    try {
      if (integration) {
        const newErrors = validateForm(integration);

        if (!!Object.values(newErrors).length) {
          setErrors(newErrors);
          return;
        }
        setSaveLoading(true);
        if (integration?.id) {
          const updatedIntegration = await hris.updateIntegration({
            id: integration.id,
            requestBody: integration,
          });
          setIntegration(updatedIntegration);
        } else {
          if (isValidForm(integration)) {
            const newIntegration = await hris.createIntegration({
              requestBody: {
                ...integration,
                type: HRISIntegrationType.WORKDAY_RAAS,
              }
            });
            setIntegration(newIntegration);
          }
        }
        showNotification('', 'Workday RaaS Settings have been saved', 'success');
        setDirty(false);
      }
    } catch (err: any) {
      if (err.status !== 400) {
        reportError(err);
      }

      const message = err.body?.details
        ? err.body.details?.body?.map(({ message }: { message: string }) => <>{message};<br /></>)
        : err.body?.message;

      showNotification('Failed to save Workday RaaS integration', message, 'error');
    } finally {
      setSaveLoading(false);
    }
  }

  const handleDeactivate = async () => {
    try {
      setDeleteLoading(true);
      if (integration?.id) {
        await hris.deleteIntegration({
          id: integration.id,
        });
        setIntegration(undefined);
      }
    } catch (err: any) {
      reportError(err);
      showNotification(isApiError(err) ? err.body.error : err.message, 'Failed to save Workday RaaS integration', 'error');
    } finally {
      setDeleteLoading(false);
    }
  }

  const handleConfigChange = (configName: HRISConfigName, event: React.ChangeEvent<HTMLInputElement>) => {
    markDirty();
    setIntegration({
      ...integration,
      [configName]: integration?.[configName] ? {
        ...integration[configName],
        [event.target.name]: event.target.value,
      } : {
        [event.target.name]: event.target.value,
      }
    })
  }

  return (
    <div className={cn.content}>
      {loading ? (
        <Loader/>
      ) : (
        <>
          <Row gutter={[12, 24]}>
            <Col xs={24}>
              <AuthForm
                form={integration?.auth}
                onChange={handleAuthChange}
                errors={errors}
              />
            </Col>
            <Col xs={24} data-testid="cronSelector">
              <ScheduleControl
                schedule={integration?.schedule}
                onChange={(value) => handleChange(value, 'schedule')}
                error={errors.schedule}
              />
            </Col>
            <Col xs={6} data-testid="timezoneSelector">
              <TimezoneSelector
                onChange={(value) => handleChange(value, 'timezone')}
                value={integration?.timezone}
                error={errors.timezone}
              />
            </Col>
            <Divider className={cn.divider}/>
            <Col xs={24}>
              <SectionHeader>
                Data types configuration
              </SectionHeader>
            </Col>
            <Col flex="auto">
              <Input
                label="Jobs report API endpoint"
                data-testid="Jobs report API endpoint_Input"
                data-sensitive="true"
                value={integration?.jobsConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.JOBS_CONFIG, event)}
                addonAfter={
                  <Button
                    color="primary"
                    variant="outlined"
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.JOBS_CONFIG)}
                    disabled={testDisabled || !integration?.jobsConfig?.url}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
            <Col flex="auto">
              <Input
                label="Employees report API endpoint"
                data-testid="Employees report API endpoint_Input"
                data-sensitive="true"
                value={integration?.employeesConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.EMPLOYEES_CONFIG, event)}
                addonAfter={
                  <Button
                    color="primary"
                    variant="outlined"
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.EMPLOYEES_CONFIG)}
                    disabled={testDisabled || !integration?.employeesConfig?.url}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
            <Col flex="auto">
              <Input
                label="Ranges report API endpoint"
                data-testid="Ranges report API endpoint_Input"
                data-sensitive="true"
                value={integration?.rangesConfig?.url}
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleConfigChange(HRISConfigName.RANGES_CONFIG, event)}
                addonAfter={
                  <Button
                    color="primary"
                    variant="outlined"
                    className={cn.testButton}
                    loading={loadingTest}
                    onClick={() => testFileEndpoint(HRISConfigName.RANGES_CONFIG)}
                    disabled={testDisabled || !integration?.rangesConfig?.url}
                    data-testid="testButton"
                  >
                    Test
                  </Button>
                }
              />
            </Col>
          </Row>
          <Row gutter={[12, 24]} justify="end" className={cn.buttons}>
            {integration?.id && (
              <Col>
                <ConfirmModal
                  content={(
                    <div>
                      Are you sure you want to deactivate Workday RaaS integration? All data file integrations will be deleted.
                    </div>
                  )}
                  onConfirm={handleDeactivate}
                >
                  <Button
                    type="primary" danger
                    loading={deleteLoading}
                    data-testid="deactivateButton"
                  >
                    Deactivate Integration
                  </Button>
                </ConfirmModal>
              </Col>
            )}
            <Col>
              <Button type="primary" onClick={handleSave} loading={saveLoading} disabled={!!integration?.id && !dirty} data-testid={`${!integration?.id ? 'activate' : 'update'}Button`}>
                {!integration?.id ? 'Activate integration' : 'Save Config'}
              </Button>
            </Col>
            {integration?.id && (
              <Col>
                <Button
                  color="primary" variant="outlined"
                  loading={loadingSync}
                  disabled={dirty}
                  onClick={handleSync}
                  data-testid="syncButton"
                >
                  Pull Reports Now
                </Button>
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  )
}
